import { useCallback, useState } from "react";
import { useDataProvider, useNotify, useRefresh } from "react-admin";
import { useNavigate } from "react-router";
import { AlertType, type DocumentTransformation, type TransformationTemplate } from "@/types";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { buildReprocessPayload } from "../utils/fieldMappingUtils";

interface UseFieldMappingSaveProps {
  /** The document transformation being edited */
  transformation: DocumentTransformation;
  /** The transformation template (optional if not yet created) */
  template: TransformationTemplate | undefined;
  /** Set of schema keys with actual user changes (not just initialization) */
  schemasWithChanges: Set<string>;
  /** Array of schema keys that passed validation */
  validSchemas: string[];
  /** Array of schema keys that failed validation */
  invalidSchemas: string[];
  /** Mapping of schema keys to their edited field mappings */
  editedMappingsBySchema: Record<string, Record<string, string[]>>;
  /** Callback to close the dialog after successful save */
  onClose: () => void;
  /** The client ID to send for reprocessing */
  clientId: string;
  /** Explicit document ID — fallback when transformation.document_id_ref is empty */
  documentId?: string;
}

/**
 * Hook to handle saving field mapping changes and triggering document reprocessing
 *
 * Handles:
 * - Validation of save prerequisites (template exists, changes made, valid schemas)
 * - Building the reprocess payload with only valid schema mappings
 * - API call to trigger document reprocessing
 * - Success/error notifications with contextual messages
 * - Navigation back to document show page
 * - Loading state management during save operation
 *
 * Save Process:
 * 1. Validates prerequisites (template, edits, valid schemas)
 * 2. Builds payload containing only schemas that passed validation
 * 3. Calls reprocess endpoint with updated field mappings
 * 4. Shows success message (including count of skipped schemas if any)
 * 5. Navigates to document show page
 * 6. Triggers data refresh
 *
 * Edge Cases Handled:
 * - No template available → Error notification, no save
 * - No changes made → Info notification, closes dialog
 * - All schemas have errors → Error notification, blocks save
 * - Some schemas have errors → Saves valid schemas, notifies about skipped ones
 *
 * @param props - Configuration for save operation
 * @returns Save handler function and loading state
 *
 * @example
 * ```tsx
 * const { handleSave, isSaving } = useFieldMappingSave({
 *   transformation,
 *   template,
 *   editedSchemas,
 *   validSchemas,
 *   invalidSchemas,
 *   editedMappingsBySchema,
 *   onClose: () => setDialogOpen(false),
 *   clientId,
 * });
 *
 * <Button onClick={handleSave} disabled={isSaving}>
 *   {isSaving ? "Saving..." : "Save & Reprocess"}
 * </Button>
 * ```
 */
export const useFieldMappingSave = ({
  transformation,
  template,
  schemasWithChanges,
  validSchemas,
  invalidSchemas,
  editedMappingsBySchema,
  onClose,
  clientId,
  documentId,
}: UseFieldMappingSaveProps) => {
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Handle save operation
   * Validates prerequisites, builds payload, calls API, and handles success/error states
   */
  const handleSave = useCallback(async () => {
    // Validate prerequisites
    if (!template) {
      notify("No template available to update", { type: AlertType.ERROR });
      return;
    }

    if (schemasWithChanges.size === 0) {
      notify("No changes to save", { type: AlertType.INFO });
      onClose();
      return;
    }

    if (validSchemas.length === 0) {
      notify("All edited schemas have validation errors. Please fix them before saving.", { type: AlertType.ERROR });
      return;
    }

    setIsSaving(true);

    try {
      // Build payload with only valid schemas
      const payload = buildReprocessPayload(
        editedMappingsBySchema,
        schemasWithChanges,
        new Set(invalidSchemas),
        clientId
      );

      // Resolve the document ID — prefer explicit prop, fall back to transformation ref
      const docId = documentId || transformation.document_id_ref;
      if (!docId) {
        notify("Cannot reprocess: document ID is missing", { type: AlertType.ERROR });
        return;
      }

      // Trigger document reprocessing with updated mappings
      await dataProvider.create(`documents/${docId}/reprocess`, {
        data: payload,
      });

      // Build success message accounting for skipped schemas
      const skippedCount = invalidSchemas.length;
      const message =
        skippedCount > 0
          ? `Document will be reprocessed with ${validSchemas.length} valid schema${validSchemas.length > 1 ? "s" : ""}. ${skippedCount} schema${skippedCount > 1 ? "s" : ""} with errors were skipped.`
          : `Document will be reprocessed with updated mappings for ${validSchemas.length} schema${validSchemas.length > 1 ? "s" : ""}`;

      notify(message, { type: AlertType.SUCCESS });

      // Navigate back to document and refresh data
      void navigate(`/documents/${docId}/show`);
      void refresh();
    } catch (error) {
      notifyError(error);
    } finally {
      setIsSaving(false);
    }
  }, [
    editedMappingsBySchema,
    validSchemas,
    invalidSchemas,
    template,
    transformation,
    onClose,
    notify,
    notifyError,
    refresh,
    dataProvider,
    navigate,
    schemasWithChanges,
  ]);

  return {
    /**
     * Trigger the save and reprocess operation
     * Performs validation checks before calling API
     */
    handleSave,

    /**
     * Whether a save operation is currently in progress
     * Use to disable UI elements during save
     */
    isSaving,
  };
};
